/* eslint-disable jsx-a11y/media-has-caption */
import React from 'react';
import PropTypes from 'prop-types';
import { CroppedImageView } from '@bingads-webui-campaign-react/cropped-image-view';
import { PlacementQualityToText } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { submitEvent } from '@bingads-webui-universal/accessibility-helper';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Badge,
} from '@fluentui/react-components';
import { TimerRegular } from '@fluentui/react-icons';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { Label } from '@bingads-webui-react/primitive';

const useStyles = makeStyles({
  container: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0px 0.6px 1.8px 0px rgba(0, 0, 0, 0.10), 0px 3.2px 7.2px 0px rgba(0, 0, 0, 0.13)',
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.overflow('hidden'),
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    fontSize: tokens.fontSizeBase200,
  },
  qualityContainer: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '5px',
    marginTop: '5px',
  },
  info: {
    position: 'absolute',
    top: '210px',
    left: tokens.spacingHorizontalS,
    backgroundColor: 'black',
    color: 'white',
    width: '52px',
    fontSize: tokens.fontSizeBase200,
    height: tokens.spacingVerticalXXL,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.overflow('hidden'),
  },
  icon: {
    fontSize: tokens.fontSizeBase400,
  },
  videoContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  video: {
    width: '100%',
    height: '100%',
  },
});

// create a description for the video from text in the scenes
const getVideoDescription = ({ videoAsset, i18n }) => {
  const { duration } = videoAsset.template;
  const texts = videoAsset.template.scenes.map(scene => scene.text.join(' '));
  return curlyTemplate(i18n.getString(_TL_('video recommendation with duration {{duration}} seconds. Description: {{description}}')))({
    duration,
    description: texts.join(' '),
  });
};

export const VideoSuggestionCard = ({
  videoAsset, resizeHeight, onSelectVideoSuggestion, i18n, showQuality,
}) => {
  const classes = useStyles();
  const [showVideo, setShowVideo] = React.useState(false);
  const src = videoAsset.thumbnailUrl;
  const { width } = videoAsset.template;
  const { height } = videoAsset.template;
  const aspectRatio = width / height;

  // convert seconds to mm:ss
  const { duration } = videoAsset.template;
  const totalSeconds = duration / 1000;
  const minutes = Math.floor((totalSeconds) / 60);
  const seconds = Math.floor((totalSeconds) % 60);
  const durationString = `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  const description = getVideoDescription({ videoAsset, i18n });

  return (
    <div className="video-suggestion-container-root">
      <div
        className={mergeClasses(classes.container, 'video-suggestion-container')}
        onClick={onSelectVideoSuggestion}
        onKeyDown={submitEvent(onSelectVideoSuggestion)}
        role="button"
        tabIndex={0}
        onMouseOver={() => setShowVideo(true)}
        onMouseOut={() => setShowVideo(false)}
        onFocus={() => setShowVideo(true)}
        onBlur={() => setShowVideo(false)}
        aria-label={description}
      >
        <CroppedImageView
          src={src}
          originalWidth={width}
          aspectRatio={aspectRatio}
          resizeImageHeight={resizeHeight}
          SourceWidth={width}
        />
        {showVideo && (
          <div className={classes.videoContainer}>
            <video
              autoPlay
              loop
              className={classes.video}
            >
              <source src={videoAsset.url} />
            </video>
          </div>
        )}
        <div className={mergeClasses(classes.overlay, 'video-suggestion-overlay')} >
          <div className={mergeClasses(classes.info, 'video-suggestion-info')} >
            <TimerRegular className={classes.icon} />
            {durationString}
          </div>
        </div>
      </div>
      {
        showQuality && (
          <div className={classes.qualityContainer}>
            <Label>{i18n.getString(_TL_('Quality:'))}</Label>
            <Badge color="success">{i18n.getString(PlacementQualityToText[videoAsset.quality])}</Badge>
          </div>
        )
      }
    </div>
  );
};

VideoSuggestionCard.propTypes = {
  videoAsset: PropTypes.shape({
    url: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    template: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number,
      duration: PropTypes.number,
      scenes: PropTypes.arrayOf(PropTypes.shape({
        text: PropTypes.arrayOf(PropTypes.string),
      })),
    }),
  }).isRequired,
  resizeHeight: PropTypes.number.isRequired,
  onSelectVideoSuggestion: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  showQuality: PropTypes.bool,
};
